import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../types/common/types';
import { GzPurchaseCardLayoutWebComponent } from '../../../../company/myCompany/purchase/purchasePage/gzPurchaseCardLayout';
import { translateFn } from '../../../../utils/translator.helper';
import { ButtonColors, ButtonFontSizes, ButtonTypes } from '../../../../ui/gzButton/types';
import { ProductLandingPageBuyConfigDataProvider } from '../../types/ProductLandingPageBuyConfig';
import { debounce } from '../../../../utils/event.utils';
import { AvailableProductTrialType } from '../../types/AvailableProductTrialType';
import { BUY_NEW_LICENSE_WINBACK_LINKS } from '../../../../company/myCompany/purchase/purchasePage/constants';
import { InfoBoxSeverity } from '../../../../ui/gzInfoBox/types';
import { SpinnerWidth } from '../../../../ui/gzProgressSpinner/types';
import { DEFAULT_VALUE_VALIDITY_YEARS } from '../constants';
import { SelectOption } from '../../../../ui/gzDropdown/types';

@customElement('gz-buy-product-card')
export class GzBuyProductCardWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  @property()
  public productName: string;

  @property()
  public productType: AvailableProductTrialType;

  @property()
  public dataProvider: ProductLandingPageBuyConfigDataProvider;

  @state()
  private seatsValue: number;

  @state()
  private validityYearsValue = DEFAULT_VALUE_VALIDITY_YEARS;

  @state()
  private initialLoading = true;

  @state()
  private initialLoadingError = false;

  @state()
  private buyPrice = {
    loading: true,
    loadingError: false,
    value: 0,
    currency: '',
  };

  @state()
  private loadingBuyButton = false;

  @state()
  private showWarningForBaseLicenseSeatsExceeded = false;

  @state()
  private showWarningForMaxProductSeatsExceeded = false;

  @state()
  private showRefreshAfterPurchaseInfoBox = false;

  /**
   * This is a counter variable that is incremented every time a request is made to fetch the price for seat upgrades.
   * It is used to ignore the results of previous requests. This is particularly useful when multiple requests are
   * initiated at roughly the same time. Even if a previous request finishes last, its result will be ignored,
   * ensuring that the data used is always from the most recent request.
   */
  private lastFetchSeatsPriceRequestId = 0;

  private fetchAndUpdateSeatsPriceFn = this.createFetchBuyPriceFn();
  private fetchAndUpdateSeatsPriceFnDebounced = debounce(this.fetchAndUpdateSeatsPriceFn, 1000);

  private minSeatsForProduct: number;
  private maxSeatsForProduct: number;
  private baseLicenseSeats: number;
  private isBaseLicenseExpired: boolean;
  private hasRenewalGracePeriodPassedForBaseLicense: boolean;
  private baseLicenseProductId: number;

  private validityDropdownOptions: SelectOption[] = [
    { title: translateFn('1 year'), value: 1 },
    { title: translateFn('2 years'), value: 2 },
    { title: translateFn('3 years'), value: 3 },
  ];

  protected async updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    if (!_changedProperties.has('productType') || !this.productType) {
      // no need to fetch initial data if productType did not change
      return;
    }

    await this.fetchInitialDataForProduct();
  }

  private async fetchInitialDataForProduct() {
    try {
      // force loading state
      this.initialLoading = true;
      this.initialLoadingError = false;

      // reset warnings when product changes
      this.resetWarnings();
      this.showRefreshAfterPurchaseInfoBox = false;
      this.isBaseLicenseExpired = false;

      const initialData = await this.dataProvider.fetchProductBuyInitialData(this.productType);

      this.minSeatsForProduct = initialData.minSeatsForProduct;
      this.maxSeatsForProduct = initialData.maxSeatsForProduct;
      this.baseLicenseSeats = initialData.baseLicenseSeats;
      this.isBaseLicenseExpired = initialData.isBaseLicenseExpired;
      this.hasRenewalGracePeriodPassedForBaseLicense = initialData.hasRenewalGracePeriodPassedForBaseLicense;
      this.baseLicenseProductId = initialData.baseLicenseProductId;

      this.setInitialSeatsValue();

      // reset initial loading state
      this.initialLoading = false;

      // reset validity when product changes
      this.validityYearsValue = DEFAULT_VALUE_VALIDITY_YEARS;

      await this.fetchAndUpdateSeatsPriceFn();
    } catch (err) {
      console.error(err);
      // reset initial loading state
      this.initialLoading = false;
      this.initialLoadingError = true;
    }
  }

  private resetWarnings() {
    this.showWarningForMaxProductSeatsExceeded = false;
    this.showWarningForBaseLicenseSeatsExceeded = false;
  }

  render(): TemplateResult {
    return html`
      <div class="container">
        <gz-title
          fontSize="${FontSizes.TITLE}"
          fontWeight="${FontWeights.MEDIUM}"
          automationId="ipb-buy-product-card-title"
        >
          ${translateFn('Buy {PRODUCT_NAME}', {
            PRODUCT_NAME: this.productName,
          })}
        </gz-title>

        <div class="inner-container">
          ${this.initialLoading
            ? this.renderInitialLoadingSpinner()
            : this.initialLoadingError
            ? this.renderInitialLoadingErrorPanel()
            : html`
                <div class="main-product-box">
                  ${this.renderProductConfiguration()}
                  <!--        -->
                  ${this.renderPrice()}
                  <!--        -->
                  <div class="buy-button-container">${this.renderBuyButton()}</div>
                </div>
              `}
        </div>
        ${this.renderWarningBox()}
        <!--        -->
        ${this.renderAfterPurchaseInfoBox()}
      </div>
    `;
  }

  private renderInitialLoadingSpinner(): TemplateResult {
    return html`
      <gz-progress-spinner
        class="mt-1 pl-4 d-flex justify-center"
        automationId="ipb-buy-product-card-initial-loading-spinner"
        .spinnerwidth=${SpinnerWidth.SMALL}
      >
      </gz-progress-spinner>
    `;
  }

  private renderInitialLoadingErrorPanel() {
    return html`
      <div class="initial-loading-error-panel">
        <gz-text
          automationId="ipb-buy-product-card-initial-error-text"
          class="ml-2"
          .fontSize=${FontSizes.PARAGRAPH_12}
        >
          ${translateFn('Data loading failed')}
        </gz-text>
        <gz-button
          automationId="ipb-buy-product-card-initial-error-reload-button"
          class="ml-2"
          .color=${ButtonColors.SECONDARY}
          .type=${ButtonTypes.OUTLINE}
          .buttonFontSize=${ButtonFontSizes.SMALL}
          @click=${this.handleReloadInitialDataButtonClicked}
        >
          ${translateFn('Reload')}
        </gz-button>
      </div>
    `;
  }

  private renderProductConfiguration(): TemplateResult {
    const minSeats = this.getMinSeatsForSeatsInput();
    const maxSeats = this.getMaxSeatsForSeatsInput();

    return html`
      <div class="d-flex flex-row align-items-center">
        <gz-labeled-content
          automationId="ipb-buy-product-card-seats-labeled-content"
          class="mt-1 mr-3"
          .label="${translateFn('Number of seats')}"
          .stacked=${true}
          .hasColon=${true}
        >
          <gz-increment-input
            automationId="ipb-buy-product-card-seats-increment-input"
            .value=${this.seatsValue}
            .inputConfig=${{
              min: minSeats,
              max: maxSeats,
            }}
            .incrementBtnValues=${[5, 10, 20]}
            @incrementInputChange=${this.handleSeatsChange}
            @maxValueReached=${this.handleMaxValueReached}
            @minValueReached=${this.handleMinValueReached}
          ></gz-increment-input>
        </gz-labeled-content>

        <gz-dropdown
          automationId="ipb-buy-product-card-validity-dropdown"
          .label=${translateFn('Validity')}
          .labelWidth="${LabelWidthValues.FULL}"
          .options=${this.validityDropdownOptions}
          .value=${DEFAULT_VALUE_VALIDITY_YEARS}
          @optionSelected=${this.handleValidityChange}
        ></gz-dropdown>
      </div>
    `;
  }

  private async handleSeatsChange(event: CustomEvent) {
    const newValue = event.detail.value;
    this.seatsValue = newValue;

    if (newValue < this.getMaxSeatsForSeatsInput()) {
      this.resetWarnings();
    }

    // show loading widget immediately (ignore debounce)
    this.setLoadingStateForPrice();

    this.fetchAndUpdateSeatsPriceFnDebounced();
  }

  private async handleMaxValueReached(event: CustomEvent) {
    if (this.baseLicenseSeats < this.maxSeatsForProduct) {
      this.showWarningForBaseLicenseSeatsExceeded = true;
    } else {
      this.showWarningForMaxProductSeatsExceeded = true;
    }
  }

  private async handleMinValueReached(event: CustomEvent) {
    // this happens when the base license has fewer seats than the minimum of seats allowed by product
    // example: base license with SBS - 1 seat
    // buy patch mng that has min 3 seats
    // seats will be locked to 3 seats, click + will show warning, click - will hide the warning
    this.showWarningForBaseLicenseSeatsExceeded = false;
  }

  private async handleValidityChange(event: CustomEvent) {
    const newValue = event.detail.newValue.value;

    if (newValue === this.validityYearsValue) {
      // do nothing when same validity is selected
      return;
    }

    this.validityYearsValue = newValue;

    // show loading widget immediately (ignore debounce)
    this.setLoadingStateForPrice();

    this.fetchAndUpdateSeatsPriceFnDebounced();
  }

  private setLoadingStateForPrice() {
    this.buyPrice = {
      ...this.buyPrice,
      loading: true,
    };
  }

  private renderPrice(): TemplateResult {
    return html`
      <div class="total-price">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.SEMIBOLD}"
          automationId="ipb-buy-product-card-price-title"
        >
          ${translateFn('Your total price') + ':'}
        </gz-title>
        <div class="d-flex flex-column">
          ${this.buyPrice.loading
            ? html`
                <gz-progress-spinner
                  class="mt-1 pl-4"
                  automationId="ipb-buy-product-card-price-loading-spinner"
                  .spinnerwidth=${SpinnerWidth.SMALL}
                >
                </gz-progress-spinner>
              `
            : this.buyPrice.loadingError
            ? html`
                <gz-button
                  automationId="ipb-buy-product-card-price-reload-button"
                  .color=${ButtonColors.SECONDARY}
                  .type=${ButtonTypes.OUTLINE}
                  .buttonFontSize=${ButtonFontSizes.SMALL}
                  @click=${this.handleReloadPriceButtonClicked}
                >
                  ${translateFn('Reload')}
                </gz-button>
              `
            : html`
                <div class="d-flex flex-row align-items-center">
                  <gz-text
                    class="w-auto"
                    fontSize="${FontSizes.HEADING_1}"
                    fontWeight="${FontWeights.SEMIBOLD}"
                    automationId="ipb-buy-product-card-price-value-text"
                  >
                    ${this.buyPrice.value}
                  </gz-text>
                  <gz-text
                    class="text-uppercase"
                    fontSize="${FontSizes.HEADING_1}"
                    automationId="ipb-buy-product-card-price-currency-text"
                  >
                    ${this.buyPrice.currency}
                  </gz-text>
                </div>
                <gz-text
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  class="w-auto"
                  automationId="ipb-buy-product-card-price-taxes-not-included-text"
                >
                  ${translateFn('Taxes not included')}
                </gz-text>
              `}
        </div>
      </div>
    `;
  }

  private getMessageForExpiredLicense(): string {
    return this.hasRenewalGracePeriodPassedForBaseLicense
      ? translateFn(
          'Your main product license has expired and is no longer eligible for renewal. To buy additional add-ons, you will need a new main product license.'
        )
      : translateFn(
          'You have to renew your main license before buying an add-on. You can find the licensing details by going to the My Company page from the user menu, and selecting the Purchase tab.'
        );
  }

  private renderBuyButton(): TemplateResult | undefined {
    const showDisabledWithTooltip = this.isBaseLicenseExpired;

    const tooltipText = this.getMessageForExpiredLicense();

    const buttonTemplate = html`
      <gz-button
        automationId="ipb-buy-product-card-proceed-to-checkout-button"
        .color=${ButtonColors.PRIMARY}
        ?disabled=${showDisabledWithTooltip || this.loadingBuyButton || this.buyPrice.loading}
        ?showSpinner=${this.loadingBuyButton}
        @click=${this.handleBuyButtonClicked}
      >
        ${translateFn('Proceed to checkout')}
      </gz-button>
    `;

    return showDisabledWithTooltip
      ? html`
        <gz-tooltip tooltipText=${tooltipText}>
          ${buttonTemplate}
        </gz-tooltip>
    `
      : buttonTemplate;
  }

  private renderWarningBox(): TemplateResult | undefined {
    // first check if base license is expired, then if seats are exceeded

    if (this.isBaseLicenseExpired) {
      return this.renderWarningBoxBaseLicenseExpired();
    }

    if (this.showWarningForMaxProductSeatsExceeded || this.showWarningForBaseLicenseSeatsExceeded) {
      return this.renderWarningBoxMaxSeatsExceeded();
    }
  }

  private renderWarningBoxMaxSeatsExceeded() {
    const message = this.showWarningForMaxProductSeatsExceeded
      ? translateFn(
          'You can buy up to {MAX_SEATS} seats. If you need more, you can expand your license. To do so, go to the My Company page from the user menu, then select the Purchase tab.',
          {
            MAX_SEATS: this.getMaxSeatsForSeatsInput(),
          }
        )
      : translateFn(
          'You can buy as many seats as your main license provides. To add more, go to the My Company page from the user menu, and select the Purchase tab.'
        );

    return html`
      <gz-info-box automationId="ipb-buy-product-card-max-seats-exceeded-info-box">
        <div class="warning-box">
          <gz-text automationId="ipb-buy-product-card-max-seats-exceeded-text" .fontSize=${FontSizes.PARAGRAPH_12}>
            ${message}
          </gz-text>
        </div>
      </gz-info-box>
    `;
  }

  private renderWarningBoxBaseLicenseExpired() {
    const gracePeriodPassed = this.hasRenewalGracePeriodPassedForBaseLicense;

    const message = this.getMessageForExpiredLicense();

    return html`
      <gz-info-box automationId="ipb-buy-product-card-base-license-expired-info-box" .severity="${InfoBoxSeverity.WARNING}">
        <div class="warning-box ${gracePeriodPassed ? 'with-button' : ''}">
          <gz-text automationId="ipb-buy-product-card-base-license-expired-text" .fontSize=${FontSizes.PARAGRAPH_12}>
            ${message}
          </gz-text>

          ${gracePeriodPassed
            ? html`
                <gz-button
                  automationId="ipb-buy-product-card-base-license-expired-buy-new-license-button"
                  class="mr-2"
                  @click=${this.handleBuyNewLicenseClick}
                >
                  ${translateFn('Buy new license')}
                </gz-button>
              `
            : ''}
        </div>
      </gz-info-box>
    `;
  }

  private renderAfterPurchaseInfoBox(): TemplateResult | undefined {
    if (!this.showRefreshAfterPurchaseInfoBox) {
      return undefined;
    }

    return html`
      <gz-info-box class="mb-1" automationId="ipb-buy-product-card-after-purchase-info-box">
        <gz-text automationId="ipb-buy-product-card-after-purchase-text" .fontSize=${FontSizes.PARAGRAPH_12}>
          ${translateFn(
            'Your changes will apply shortly after the purchase. Refresh your browser page to update the displayed information.'
          )}
        </gz-text>
        <gz-button
          type="${ButtonTypes.QUIET}"
          color="${ButtonColors.PRIMARY}"
          class="mt-1"
          buttonheight="small"
          buttonfontsize="small"
          automationId="ipb-buy-product-card-after-purchase-reload-button"
          @click="${() => window.location.reload()}"
        >
          ${translateFn('Refresh page')}
        </gz-button>
      </gz-info-box>
    `;
  }

  private async handleBuyNewLicenseClick() {
    const buyUrl = BUY_NEW_LICENSE_WINBACK_LINKS[this.baseLicenseProductId];

    if (buyUrl) {
      window.open(buyUrl, '_blank');
    }
  }

  private async handleBuyButtonClicked() {
    this.loadingBuyButton = true;

    try {
      const response = await this.dataProvider.fetchProductBuyUrl(
        this.productType,
        this.seatsValue,
        this.validityYearsValue
      );

      this.loadingBuyButton = false;

      window.open(response.url, '_blank');

      this.resetWarnings();

      this.showRefreshAfterPurchaseInfoBox = true;
    } finally {
      // reset loading state even if it fails (so that user can click again)
      this.loadingBuyButton = false;
    }
  }

  private async handleReloadInitialDataButtonClicked() {
    await this.fetchInitialDataForProduct();
  }

  private async handleReloadPriceButtonClicked() {
    await this.fetchAndUpdateSeatsPriceFn();
  }

  private createFetchBuyPriceFn() {
    return async () => {
      this.buyPrice = {
        ...this.buyPrice,
        loading: true,
      };

      const currentRequestId = this.lastFetchSeatsPriceRequestId + 1;
      this.lastFetchSeatsPriceRequestId = currentRequestId;

      try {
        const buyPriceResponse = await this.dataProvider.fetchProductBuyPrice(
          this.productType,
          this.seatsValue,
          this.validityYearsValue
        );

        if (this.lastFetchSeatsPriceRequestId > currentRequestId) {
          // not the last request, ignore result
          return;
        }

        this.buyPrice = {
          loading: false,
          loadingError: false,
          value: buyPriceResponse.price,
          currency: buyPriceResponse.currency,
        };
      } catch (err) {
        console.error(err);
        this.buyPrice = {
          ...this.buyPrice,
          loading: false,
          loadingError: true, // show reload button
        };
      }
    };
  }

  private getMinSeatsForSeatsInput(): number {
    return this.minSeatsForProduct;
  }

  private getMaxSeatsForSeatsInput(): number {
    const baseLicenseSeats = this.baseLicenseSeats;
    const maxSeatsForProduct = this.maxSeatsForProduct;
    const minSeats = this.minSeatsForProduct;

    if (baseLicenseSeats > maxSeatsForProduct) {
      return maxSeatsForProduct;
    } else if (baseLicenseSeats < minSeats) {
      return minSeats;
    } else {
      return baseLicenseSeats;
    }
  }

  private setInitialSeatsValue() {
    const baseLicenseSeats = this.baseLicenseSeats;
    const maxSeatsForProduct = this.maxSeatsForProduct;
    const minSeatsForProduct = this.minSeatsForProduct;

    if (baseLicenseSeats > maxSeatsForProduct) {
      // handle case when base license already has seats which exceed max seats of product
      this.seatsValue = maxSeatsForProduct;
      this.showWarningForMaxProductSeatsExceeded = true;
    } else if (baseLicenseSeats < minSeatsForProduct) {
      this.seatsValue = minSeatsForProduct;
    } else {
      this.seatsValue = baseLicenseSeats;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-buy-product-card': GzPurchaseCardLayoutWebComponent;
  }
}
